import * as PIXI from "pixi.js";
import {AbstractGameState} from "./AbstractGameState";
import {AssemblySlotButton} from "../GameObjects/Assembly/AssemblySlotButton";
import {Container} from "../Core/Container";
import {ButtonMute} from "../GameObjects/ButtonMute";
import {Button, CheckmarkButton, HomeButton} from "../GameObjects/Button";
import {StateName} from "../Data/StateName";
import {ItemType} from "../Data/ItemModel";
import {hud} from "../Layout/HudLayout";
import {VoiceOverManager} from "../Managers/VoiceOverManager";
import {AssemblyModel} from "../GameObjects/Assembly/AssemblyModel";
import {Assembly} from "../GameObjects/Assembly/Assembly";
import {AssemblyItems, AssemblyLayouts} from "../GameObjects/Assembly/AssemblyItems";
import {Callback, Dict} from "../Core/Reflection";
import {Ease} from "../Core/Animatable";
import {Keyboard} from "../GameObjects/Keyboard";
import {playVO, ReactionVO, voOptionsFacts} from "../Data/VoiceOver";
import {Sprite} from "../Core/Sprite";

export class AssemblyScreen<T extends string> extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new Sprite());
    public assembly!: Assembly<any>;

    public readonly buttons: AssemblySlotButton<T>[] = [];
    public readonly buttonOk = this.main.addChild(new CheckmarkButton());
    public readonly buttonHome = this.main.addChild(new HomeButton());
    public readonly buttonMute = this.addChild(new ButtonMute());

    private _model!: AssemblyModel<T>;
    private _lastSlotClicked?: T;
    protected _factsDisabled = false;

    init(pSize: PIXI.Point, model: AssemblyModel<T>) {
        super.init(pSize, model);

        this._model = model;

        this.assembly = this.main.addChild(new (AssemblyItems[model.assemblyType])());
        this.assembly.zIndex = this.background.zIndex + 1;
        this.buttons.push(...this.assembly.getEditableSlots().map(slot => this.main.addChild(new AssemblySlotButton(slot))));

        (AssemblyLayouts[model.assemblyType] as Callback).call(this);
        hud.call(this);
        this.main.scale.set(0.5);

        this.assembly.setModel(model.slots);

        const hoverVO = this.getSlotHoverVO();
        for (const button of this.buttons) {
            if (hoverVO) {
                button.hoverVO = hoverVO[button.slotType];
                button.hoverVOOnce = true;
            }
            button.onClick.add(() => this.onClickSlotButton(button));
        }
        this.buttonHome.onClick.add(() => {
            this.returnHome(model, false);
        });
        this.buttonOk.onClick.add(() => {
            this.buttonOk.stopPulse();
            this.returnHome(model, true);
        });
        this.buttonOk.visible = false;

        const vo = this.getStartVO();
        if (vo) {
            playVO(vo, () => {
                this.assembly.delayedCall(3, () => {
                    const facts = this.getFactVO();
                    if (facts && !this._factsDisabled) {
                        playVO(this.gameManager.nextFactVO(model.assemblyType as any, facts), voOptionsFacts);
                    }
                })
            });
        }

        if (this.gameManager.hasItem(model.assemblyType as any)) {
            this.showOkButton();
        }

        this.initKeyboard();
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]) {
        VoiceOverManager.instance.stopVO();
        super.destroy(pOptions);
    }

    protected onClickSlotButton(button: AssemblySlotButton<T>) {
        this.assembly.nextSlot(button.slotType);
        if (this._lastSlotClicked !== button.slotType) {
            this._lastSlotClicked = button.slotType;
            if (this.isAnySlotClicked()) {
                this.showOkButton();
            }
        }
    }

    private isAnySlotClicked() {
        return !!this._lastSlotClicked;
    }

    protected getStartVO(): (string | number)[] | undefined {
        return undefined;
    }

    protected getFactVO(): string[] | undefined {
        return undefined;
    }

    protected getSlotHoverVO(): Dict<T, string> | undefined {
        return undefined;
    }

    protected getAssemblyCompleteVO(): string[] | undefined {
        return undefined;
    }

    protected showOkButton() {
        if (!this.buttonOk.visible) {
            this.showButtons([this.buttonOk]);
            this.buttonOk.pulse();
            this.startActivityCompleteIdle();
        }
    }

    protected playCompleteVO(cb?: Callback) {
        const completeVO = this.getAssemblyCompleteVO();
        if (completeVO && completeVO.length > 0) {
            playVO([...ReactionVO(), this.gameManager.nextCompleteActivityVO(this._model.assemblyType as any, completeVO)], cb);
        } else if (cb) {
            cb();
        }
    }

    protected returnHome(model: AssemblyModel<any>, submitted: boolean) {
        const itemType = model.assemblyType as any as ItemType;
        const newItem = !this.gameManager.getItem(itemType);
        if (submitted || newItem) {
            this.gameManager.addItem({
                itemType: itemType,
                itemData: {
                    assemblyType: model.assemblyType,
                    slots: this.assembly.getModel(),
                    render: this.assembly.getRender()
                }
            });
        }
        this.gotoState(StateName.Home, {activityComplete: submitted});
        this.clearKeyboard();
    }

    protected hideButtons(buttons: Button[], cb?: Callback) {
        let d = 0;
        buttons.forEach(it => {
            const y = it.y;
            it.enabled = false;
            it.moveYTo(this._size.x + it.height * 0.5, 0.2, Ease.sineIn, {
                delay: d += 0.1,
                onComplete: () => {
                    it.y = y;
                    it.visible = false;
                    if (cb && it === buttons[buttons.length - 1]) cb();
                }
            });
        });
    }

    protected showButtons(buttons: Button[], cb?: Callback) {
        let d = 0;
        buttons.forEach(it => {
            it.visible = true;
            it.moveYFrom(this._size.y + it.height * 0.5, 0.4, Ease.backOut, {
                delay: d += 0.2,
                onComplete: () => {
                    it.enabled = true;
                    if (cb && it === buttons[buttons.length - 1]) cb();
                }
            });
        });
    }

    protected initKeyboard() {
        Keyboard.clear();
        Keyboard.add(
            ...this.buttons,
            this.buttonOk,
            this.buttonHome,
            this.buttonMute
        );
    }

    protected clearKeyboard() {
        Keyboard.clear();
        Keyboard.add(
            this.buttonMute
        );
    }
}